/** Lightbox modal for previewing images and videos in Storage — click-to-expand with keyboard nav */

import { useEffect, useCallback, useMemo } from 'react'
import { X, Download, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '../../lib/utils'
import { API_BASE } from '../../lib/api-client'
import { useAppStore } from '../../stores/app-store'
import type { StorageFile } from '../../hooks/use-storage'

interface StoragePreviewModalProps {
  file: StorageFile | null
  files: StorageFile[]
  onClose: () => void
  onNavigate: (file: StorageFile) => void
}

/** Check if a content type is previewable.
 *  PDFs render natively in <iframe>. See #197. */
export function isPreviewable(contentType: string): boolean {
  return (
    contentType.startsWith('image/') ||
    contentType.startsWith('video/') ||
    contentType === 'application/pdf'
  )
}

export function StoragePreviewModal({ file, files, onClose, onNavigate }: StoragePreviewModalProps) {
  const { theme } = useAppStore()
  const isDark = theme === 'dark'

  // Filter to only previewable files for navigation (memoized to stabilise useCallback deps)
  const previewableFiles = useMemo(() => files.filter((f) => isPreviewable(f.contentType)), [files])
  const currentIndex = file ? previewableFiles.findIndex((f) => f.id === file.id) : -1

  const goPrev = useCallback(() => {
    if (currentIndex <= 0) return
    onNavigate(previewableFiles[currentIndex - 1])
  }, [currentIndex, previewableFiles, onNavigate])

  const goNext = useCallback(() => {
    if (currentIndex === -1 || currentIndex >= previewableFiles.length - 1) return
    onNavigate(previewableFiles[currentIndex + 1])
  }, [currentIndex, previewableFiles, onNavigate])

  // Keyboard navigation + body scroll lock while modal is open
  useEffect(() => {
    if (!file) return
    document.body.style.overflow = 'hidden'
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') goPrev()
      if (e.key === 'ArrowRight') goNext()
    }
    window.addEventListener('keydown', handler)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', handler)
    }
  }, [file, onClose, goPrev, goNext])

  if (!file) return null

  // Append `?inline=1` so we get a *fresh* response key. The serving route
  // sets Cache-Control: immutable for one year — without busting the URL,
  // browsers that fetched the file before the inline-disposition fix will
  // keep replaying the old `attachment` response and re-trigger downloads.
  const fileUrl = `${API_BASE}/api/files/${file.fileKey}?inline=1`
  const isImage = file.contentType.startsWith('image/')
  const isVideo = file.contentType.startsWith('video/')
  const isPdf = file.contentType === 'application/pdf'
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex < previewableFiles.length - 1

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Content container */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Preview: ${file.filename}`}
        className="relative flex max-h-[90vh] max-w-[90vw] flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top toolbar */}
        <div className="absolute -top-10 left-0 right-0 flex items-center justify-between">
          <span className="truncate text-xs text-white/70">{file.filename}</span>
          <div className="flex items-center gap-2">
            <a
              href={fileUrl}
              download={file.filename}
              className="rounded-md p-1.5 text-white/70 hover:bg-white/10 hover:text-white"
              title="Download"
            >
              <Download className="h-4 w-4" />
            </a>
            <button
              onClick={onClose}
              className="rounded-md p-1.5 text-white/70 hover:bg-white/10 hover:text-white"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Media */}
        {isImage && (
          <img
            key={file.id}
            src={fileUrl}
            alt={file.filename}
            className="max-h-[85vh] max-w-[85vw] rounded-lg object-contain"
          />
        )}
        {isVideo && (
          <video
            key={file.id}
            src={fileUrl}
            controls
            autoPlay
            muted
            className="max-h-[85vh] max-w-[85vw] rounded-lg"
          >
            <track kind="captions" />
          </video>
        )}
        {isPdf && (
          <iframe
            key={file.id}
            src={fileUrl}
            title={`Preview: ${file.filename}`}
            className="h-[85vh] w-[80vw] max-w-[1200px] rounded-lg bg-white"
          />
        )}

        {/* Prev/Next navigation */}
        {hasPrev && (
          <button
            onClick={goPrev}
            className="absolute left-0 top-1/2 -translate-x-12 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white/70 hover:bg-white/20 hover:text-white"
            title="Previous"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        {hasNext && (
          <button
            onClick={goNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 rounded-full bg-white/10 p-2 text-white/70 hover:bg-white/20 hover:text-white"
            title="Next"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}

        {/* Counter */}
        {previewableFiles.length > 1 && (
          <div className={cn('mt-3 rounded-full px-3 py-1 text-xs', isDark ? 'bg-white/10 text-white/60' : 'bg-black/30 text-white/80')}>
            {currentIndex + 1} / {previewableFiles.length}
          </div>
        )}
      </div>
    </div>
  )
}
